import { useCallback, useEffect, useRef, useState } from "react";
import CustomInput from "../../components/CustomInput/CustomInput";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { fetchPaymentHistories } from "../../store/actions/payment.action";
import { useSnackbar } from "notistack";
import dayjs from "@lib/dayjs";
import { useLocation, useNavigate } from "react-router-dom";
import ChevronDown from "../../assets/svg/ChevronDown";
import ChevronUp from "../../assets/svg/ChevronUp";
import CustomDateRangePicker from "../../components/CustomDateRangePicker";
import DownloadCSV from "../../components/DownloadCSV/DownloadCSV";

const Spinner = ({ size = "small" }) => {
  const sizeClasses = {
    small: "w-4 h-4",
    medium: "w-6 h-6",
    large: "w-8 h-8",
  };

  return (
    <div className={`${sizeClasses[size]} animate-spin`}>
      <div className="h-full w-full border-4 border-gray-200 border-t-primary rounded-full" />
    </div>
  );
};

const TransactionPage = () => {
  const currentTabData = [];
  const {
    control,
    formState: { errors },
    watch,
    reset,
  } = useForm({
    defaultValues: {
      search: undefined,
      searchBy: "Search by",
    },
  });
  const fields = watch();
  const dispatch = useDispatch();
  const {
    paymentHistories,
    hasMore,
    currentPage,
    loading: reduxLoading,
  } = useSelector((state) => state.payment);
  const [searchTerm, setSearchTerm] = useState("");
  const searchDebounceRef = useRef(null);
  const [paginationLoading, setPaginationLoading] = useState(false);
  const observer = useRef(null);
  const { enqueueSnackbar } = useSnackbar();
  const initialized = useRef(false);
  const wasSearching = useRef(false);
  const navigate = useNavigate();
  const [isTypeDropDownOpen, setIsTypeDropDownOpen] = useState(false);
  const dateRangeRef = useRef(false);
  const typeFilterRef = useRef(false);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [dateFilterName, setDateFilterName] = useState("Date Filter");
  const [typeFilterName, setTypeFilterName] = useState("Type");
  const [hasActiveFilters, setHasActiveFilters] = useState(false);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const location = useLocation();

  const paymentHistoryTypes = [
    { jobpost: "Job Post" },
    { referralpayment: "Referral Payment" },
    { subscription: "Subscription" },
  ];

  // handle search with debounce
  const handleSearch = useCallback((value) => {
    setSearchTerm(value);
    wasSearching.current = true;

    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);

    searchDebounceRef.current = setTimeout(() => {
      // Check if typeFilterName has a non-default value and find its key
      let typeKey = null;
      if (typeFilterName !== "Type") {
        const selectedType = paymentHistoryTypes.find((type) => {
          const label = Object.values(type)[0];
          return label === typeFilterName;
        });

        if (selectedType) {
          typeKey = Object.keys(selectedType)[0];
        }
      }

      dispatch(fetchPaymentHistories(1, typeKey, value));
    }, 1000);
  }, []);

  useEffect(() => {
    if (location.state && location.state.type) {
      if (location.state.type == "subscription")
        handleTypeFilterSelection("Subscription", location.state.type);
    }
    const handleClickOutside = (event) => {
      // Handle date picker outside click
      if (isDatePickerOpen) {
        const calendarContainer = document.querySelector(
          ".date-picker-container"
        );
        if (calendarContainer && !calendarContainer.contains(event.target)) {
          setIsDatePickerOpen(false);
        }
        return;
      }

      if (
        typeFilterRef.current &&
        !typeFilterRef.current.contains(event.target)
      ) {
        setIsTypeDropDownOpen(false);
      }
    };

    // Add listener
    document.addEventListener("mousedown", handleClickOutside);
    // check if we've already initialized
    if (initialized.current) {
      // if we're returning to the page and last action was search
      if (wasSearching.current) {
        wasSearching.current = false; // reset the flag
        dispatch(fetchPaymentHistories(1, true));
      }
      return;
    }

    // set initialized to true immediately to prevent double fetch
    initialized.current = true;

    // fetch initial data using cache if available
    dispatch(fetchPaymentHistories(1));

    return () => {
      // Only clear search term on unmount, don't force refetch
      setSearchTerm("");
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const debounce = (func, delay) => {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => func(...args), delay);
    };
  };

  // for unnecessary api calls
  const debouncedFetch = useCallback(
    debounce((nextPage, paymentType) => {
      // prevent multiple calls while loading
      if (paginationLoading || reduxLoading) return;
      setPaginationLoading(true);
      dispatch(fetchPaymentHistories(nextPage, paymentType)).finally(() =>
        setPaginationLoading(false)
      );
    }, 500),
    []
  );
  // const currentTabData = paymentHistoriesByType[tabs[activeTab].type];

  // for infinite scroll
  const lastHistoryElementRef = useCallback(
    (node) => {
      if (reduxLoading || paginationLoading) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver(
        (entries) => {
          if (entries[0]?.isIntersecting && hasMore && !paginationLoading) {
            console.log("currentPage", currentPage);
            console.log("debouncedFetch");
            const nextPage = currentPage + 1;
            debouncedFetch(nextPage);
          }
        },
        {
          threshold: 0.5, // reduced threshold for better performance
          rootMargin: "200px", // add some margin to trigger earlier
        }
      );
      if (node) observer.current.observe(node);
    },
    [reduxLoading, paginationLoading, debouncedFetch]
  );

  const handleDateFilterSelection = () => {
    // Directly open the date picker
    setIsDatePickerOpen(true);
  };

  const handleTypeFilterSelection = (label, key) => {
    setTypeFilterName(label);
    setIsTypeDropDownOpen(false);
    dispatch(
      fetchPaymentHistories(1, key, searchTerm !== "" ? searchTerm : null)
    );
  };

  // reset all filters
  const resetAllFilters = () => {
    setSearchTerm("");
    setDateFilterName("Date Filter");
    setTypeFilterName("Type");
    setStartDate(null);
    setEndDate(null);
    setIsTypeDropDownOpen(false);
    setIsDatePickerOpen(false);
    setHasActiveFilters(false);

    // reset the form input
    reset({ search: "" });

    // fetch date without any filters
    dispatch(fetchPaymentHistories(1, null));
  };

  const handleDatePickerApply = (start, end) => {
    // Only update parent state when Apply is clicked
    setStartDate(start);
    setEndDate(end);
    setIsDatePickerOpen(false);

    if (start && end) {
      setDateFilterName(
        `${dayjs(start).format("DD MMM")} - ${dayjs(end).format("DD MMM")}`
      );

      // Convert dates to UTC for API call
      const startDateUTC = dayjs(start).utc().startOf("day").toDate();
      const endDateUTC = dayjs(end).utc().endOf("day").toDate();

      // Fetch data with date range in UTC
      dispatch(
        fetchPaymentHistories(1, null, searchTerm, startDateUTC, endDateUTC)
      );
    }
  };

  const handleDatePickerCancel = () => {
    // Just close the picker, don't update any dates
    setIsDatePickerOpen(false);
  };

  // Format date range based on input field state
  const formatDateRangeForInput = () => {
    if (startDate && endDate) {
      if (isDatePickerOpen) {
        // Show full date range when expanded
        return `${dayjs(startDate).format("DD MMM, YYYY")} - ${dayjs(
          endDate
        ).format("DD MMM, YYYY")}`;
      } else {
        // Show shorter format when collapsed
        return `${dayjs(startDate).format("DD MMM")} - ${dayjs(endDate).format(
          "DD MMM"
        )}`;
      }
    }
    if (startDate) {
      return isDatePickerOpen
        ? dayjs(startDate).format("DD MMM, YYYY")
        : dayjs(startDate).format("DD MMM");
    }
    return "Date Filter";
  };

  // Prepare CSV data matching table structure
  const prepareCsvData = () => {
    if (!Array.isArray(paymentHistories)) {
      return [];
    }
    return (
      paymentHistories?.map((paymentHistory) => ({
        Reference: paymentHistory?.refNo || "NA",
        Description:
          paymentHistory?.type === "jobpost"
            ? "Job Post"
            : paymentHistory?.type === "referralpayment"
            ? "Referral Payment"
            : "Subscription",
        Name: paymentHistory?.recipientDetails
          ? `${paymentHistory?.recipientDetails?.firstName} ${paymentHistory?.recipientDetails?.lastName}`
          : "COMTRACKED LTD",
        Status:
          // pravat
          paymentHistory?.type === "subscription"
            ? paymentHistory?.subscriptionInvoiceStatus[0]?.toUpperCase() +
              paymentHistory?.subscriptionInvoiceStatus?.substr(1)
            : (() => {
                const status = paymentHistory?.status;
                switch (status) {
                  case "requires_payment_method":
                    return "Payment Required";
                  case "succeeded":
                    return "Paid";
                  case "processing":
                    return "Processing";
                  case "requires_action":
                    return "Action Required";
                  default:
                    return status?.replace(/_/g, " ");
                }
              })(),
        "Amount (GBP)": paymentHistory?.totalAmount || 0,
        "Date & Time": dayjs(new Date(paymentHistory?.createdAt)).format(
          "DD MMM YYYY HH:mm"
        ),
      })) || []
    );
  };

  const handleRowClick = (id, type) => {
    navigate(`/transactions/details/${id}/${type}`);
  };

  return (
    <div className="flex flex-col rounded-lg bg-white w-full flex-grow p-4 gap-2 overflow-scroll">
      <hr className="border-t border-gray-300 pt-4" />
      {/* Search and filter section */}
      <div className="flex items-center gap-5 bg-[#F1F5F9] rounded-md px-4 py-3">
        {/* Search filter */}
        <div className="flex items-start">
          <CustomInput
            key={fields.searchBy}
            name="search"
            control={control}
            className="border-none flex-shrink-0 w-[250px]"
            errors={errors}
            placeholder="Search Reference"
            type={"text"}
            value={searchTerm}
            onChange={(e) => {
              const value = e.target?.value;
              handleSearch(value);
              setHasActiveFilters(value !== "");
            }}
            rules={{
              onChange: (e) => {
                const value = e.target?.value;
                handleSearch(value);
                setHasActiveFilters(value !== "");
              },
            }}
          />
        </div>

        {/* Date filter */}
        <div
          className="flex flex-col gap-0 items-start relative"
          ref={dateRangeRef}
        >
          <button
            className={`flex gap-2 border rounded-lg bg-white px-4 py-2 justify-between cursor-pointer transition-all duration-300 ease-in-out`}
            style={{
              width: isDatePickerOpen ? "432px" : "200px", // Match new calendar width (w-36 + w-72 = 144px + 288px = 432px)
            }}
            onClick={handleDateFilterSelection}
          >
            <label className="text-charcoal font-medium text-sm cursor-pointer truncate w-full text-left">
              {formatDateRangeForInput()}
            </label>
            <svg
              className="w-5 h-5 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
          </button>

          {/* Custom Date Range Picker positioned below button */}
          {isDatePickerOpen && (
            <div className="absolute top-full z-30 date-picker-container transition-all duration-300 ease-in-out opacity-100 translate-y-0 scale-100 pointer-events-auto">
              <CustomDateRangePicker
                startDate={startDate}
                endDate={endDate}
                onApply={handleDatePickerApply}
                onCancel={handleDatePickerCancel}
                isOpen={isDatePickerOpen}
                onToggle={() => setIsDatePickerOpen(!isDatePickerOpen)}
                className="w-full"
                calendarClassName="bg-white shadow-lg"
                placeholder="Select Date Range"
              />
            </div>
          )}
        </div>

        {/* Type filter */}
        <div
          className="flex flex-col gap-0 items-start relative"
          ref={typeFilterRef}
        >
          <button
            className={`flex gap-2 border rounded-lg bg-white px-4 py-2 justify-between cursor-pointer transition-all duration-300 ease-in-out`}
            style={{ width: isTypeDropDownOpen ? "180px" : "150px" }}
            onClick={() => setIsTypeDropDownOpen(!isTypeDropDownOpen)}
          >
            <label className="text-charcoal font-medium text-sm cursor-pointer truncate w-full text-left">
              {typeFilterName}
            </label>

            {isTypeDropDownOpen ? (
              <ChevronUp className={"w-4 h-4 delay-300"} fill={"#2e2e2e"} />
            ) : (
              <ChevronDown className={"w-5 h-5 delay-300"} fill={"#2e2e2e"} />
            )}
          </button>
          {isTypeDropDownOpen && (
            <div
              className={`absolute top-full z-20 transition-all duration-300 ease-in-out ${
                isTypeDropDownOpen
                  ? "opacity-100 translate-y-0 scale-100 pointer-events-auto"
                  : "opacity-0 -translate-y-2 scale-95 pointer-events-none"
              }`}
            >
              <div className="flex flex-col justify-center border rounded-lg bg-white w-[180px]">
                {paymentHistoryTypes.map((item, index) => {
                  const key = Object.keys(item)[0];
                  const label = item[key];
                  return (
                    <>
                      <span
                        className="cursor-pointer hover:bg-gray-100 px-4 py-2 text-sm text-charcoal font-medium transition-colors duration-150 ease-in-out"
                        key={index}
                        onClick={() => handleTypeFilterSelection(label, key)}
                      >
                        {label}
                      </span>
                    </>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Clear all button - only show when filters are active */}
        {(hasActiveFilters ||
          dateFilterName !== "Date Filter" ||
          typeFilterName !== "Type") && (
          <button
            className="rounded-lg px-2 py-1 bg-red-500 hover:bg-red-700 text-white flex items-center gap-2 ml-auto"
            onClick={resetAllFilters}
          >
            <span>Clear All</span>
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        )}
      </div>
      {/* List container */}
      {reduxLoading && !paginationLoading ? (
        <div className="gap-2.5 flex mt-16 flex-col">
          {Array.from({ length: 10 }, (x, i) => i).map((key) => {
            return (
              <div key={key} className="flex justify-between">
                <div
                  style={{
                    borderWidth: 1,
                  }}
                  className="flex rounded-lg w-full pl-3 pr-8 py-4 gap-1.5"
                >
                  <div className="h-3 rounded bg-gray-200 w-1/4"></div>
                  <div className="h-3 rounded bg-gray-200 w-1/4"></div>
                  <div className="h-3 rounded bg-gray-200 w-1/4"></div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="flex flex-col mt-4 max-h-fit overflow-y-scroll w-full">
          {Array.isArray(paymentHistories) && paymentHistories?.length ? (
            <table className="w-full table-fixed border-separate border-spacing-y-2">
              <thead>
                <tr>
                  <th
                    className="text-xs text-charcoal font-semibold text-left w-[12%]
                  border-b border-gray-300 pb-2"
                  >
                    Reference
                  </th>
                  <th
                    className="text-xs text-charcoal font-semibold text-left w-[25%]  
                  border-b border-gray-300 pb-2"
                  >
                    Description
                  </th>
                  <th
                    className="text-xs text-charcoal font-semibold text-left w-[25%]
                    border-b border-gray-300 pb-2"
                  >
                    Name
                  </th>
                  <th
                    className="text-xs text-charcoal font-semibold text-left w-[13%]  
                  border-b border-gray-300 pb-2"
                  >
                    Status
                  </th>
                  <th
                    className="text-xs text-charcoal font-semibold text-left w-[12%] 
                   border-b border-gray-300 pb-2"
                  >
                    Amount
                  </th>
                  <th
                    className="text-xs text-charcoal font-semibold text-left w-[13%] 
                   border-b border-gray-300 pb-2"
                  >
                    Date
                  </th>
                  <th className="text-sm font-medium w-[12%]  border-b border-gray-300 pb-2">
                    {/* <DownloadCSV
                      data={prepareCsvData()}
                      fileName={`payment-histories`}
                      disabled={
                        !paymentHistories || paymentHistories.length === 0
                      }
                    /> */}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr></tr>
                <tr></tr>
                {paymentHistories?.map((paymentHistory, index) => {
                  console.log(
                    "payment history",
                    paymentHistory?.type,
                    paymentHistory
                  );
                  return paymentHistory?.type !=='subscription' && (
                    <tr
                      key={paymentHistory._id}
                      ref={
                        index === paymentHistories?.length - 1
                          ? lastHistoryElementRef
                          : null
                      }
                      onClick={() =>
                        handleRowClick(paymentHistory._id, paymentHistory.type)
                      }
                      className="hover:text-secondary hover:italic font-medium"
                    >
                      <td
                        className={`text-left text-xs  truncate 
                          ${index == 0 ? "border-y py-2 mt-4" : "border-b pb-2"}
                         border-gray-200 cursor-pointer `}
                      >
                        {paymentHistory?.refNo ? paymentHistory?.refNo : "NA"}
                      </td>
                      <td
                        className={`text-left text-xs  truncate  ${
                          index == 0 ? "border-y py-2 mt-4 " : "border-b pb-2"
                        }
                         border-gray-200 cursor-pointer `}
                      >
                        {paymentHistory?.type == "jobpost"
                          ? "Job Post"
                          : paymentHistory?.type == "referralpayment"
                          ? "Referral Payment"
                          : "Subscription"}
                      </td>

                      <td
                        className={`text-left text-xs truncate ${
                          index == 0 ? "border-y py-2" : "border-b pb-2"
                        }
                         border-gray-200 cursor-pointer`}
                      >
                        {paymentHistory?.recipientDetails
                          ? `${paymentHistory?.recipientDetails?.firstName} ${paymentHistory?.recipientDetails?.lastName}`
                          : "COMTRACKED LTD"}
                      </td>
                      <td
                        className={`text-left text-xs  ${
                          index == 0 ? "border-y py-2" : "border-b pb-2"
                        }
                         border-gray-200 cursor-pointer `}
                      >
                        {/* pravat */}
                        {paymentHistory?.type === "subscription"
                          ? (paymentHistory?.subscriptionInvoiceStatus[0]?.toUpperCase() ||
                              "") +
                            (paymentHistory?.subscriptionInvoiceStatus?.substr(
                              1
                            ) || "")
                          : (() => {
                              const status = paymentHistory?.status;
                              switch (status) {
                                case "requires_payment_method":
                                  return "Payment Required";
                                case "succeeded":
                                  return "Paid";
                                case "processing":
                                  return "Processing";
                                case "requires_action":
                                  return "Action Required";
                                default:
                                  return status?.replace(/_/g, " ");
                              }
                            })()}
                      </td>
                      <td
                        className={`text-left text-xs  truncate  ${
                          index == 0 ? "border-y py-2" : "border-b pb-2"
                        }
                         border-gray-200 cursor-pointer `}
                      >
                        £{paymentHistory?.totalAmount || 0}
                      </td>
                      <td
                        className={`text-left text-xs  truncate  ${
                          index == 0 ? "border-y py-2" : "border-b pb-2"
                        }
                         border-gray-200 cursor-pointer `}
                      >
                        {dayjs(new Date(paymentHistory?.createdAt)).format(
                          "DD MMM, HH:mm"
                        )}
                      </td>
                      <td
                        className={` ${
                          index == 0 ? "border-y py-2" : "border-b pb-2"
                        }
                         border-gray-200 cursor-pointer`}
                      ></td>
                    </tr>
                  );
                })}
                {/* Add pagination loader*/}
                {paginationLoading && (
                  <tr>
                    <td colSpan="6" className="text-center py-4">
                      <div className="flex justify-center items-center">
                        <Spinner size="medium" />
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          ) : (
            <div className="flex justify-center items-center mt-20">
              <span className="m-auto font-medium text-xs">
                No transaction histories found.
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TransactionPage;
